import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useBlocker } from '@tanstack/react-router';

import { DiscardChangesDialog } from '@/shared/components/DiscardChangesDialog';

/**
 * Warn before unsaved work is thrown away (FR-046).
 *
 * Two exits to cover, and they are different mechanisms. An in-app navigation
 * is the router's to intercept, and is asked about in-app — the same "Discard
 * changes?" confirmation every form uses (spec 129 FR-006). Closing the tab is
 * the browser's, and the most a page may do there is set `returnValue` and let
 * the browser ask in its own words — no custom message, by design, since sites
 * abused it.
 *
 * **Silent for an untouched draft.** A workflow the user has just created and
 * not yet edited is not dirty, so leaving it challenges nobody. A guard that
 * fired on every exit would train people to dismiss it without reading, which
 * is worse than having none — the one time it mattered would look like all the
 * others.
 *
 * **Save draft and leave** (spec 144 FR-001, FR-002) is the dialog's default:
 * it saves, and only once the save has succeeded lets the navigation the
 * person started go on. A refused save keeps them here, with the dialog closed
 * so the reason — shown by the save itself, where Save shows it — is what they
 * look at next.
 *
 * Returns the confirmation; render it on the page.
 */
export function useUnsavedGuard(
  dirty: boolean,
  {
    onSaveDraft,
    onDiscard,
  }: {
    /** Saves the draft; resolves to whether it succeeded. */
    onSaveDraft: () => Promise<boolean>;
    /** The edits are being dropped. */
    onDiscard?: () => void;
  },
): ReactNode {
  const [saving, setSaving] = useState(false);

  // `withResolver` holds the navigation while the dialog asks: the router waits
  // on `proceed` or `reset` rather than on a synchronous answer, which is what
  // used to force the native `window.confirm`.
  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) => {
      if (!dirty) return false;

      // Only a navigation that actually *leaves* the builder. Selecting a node
      // writes `?sel=`, which is a navigation to the router but not to the
      // user — challenging them on every click once the draft is dirty would
      // make the page unusable at exactly the point the work is worth keeping.
      return current.pathname !== next.pathname;
    },
    enableBeforeUnload: false,
    withResolver: true,
  });

  useEffect(() => {
    if (!dirty) return;

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Assigning `returnValue` is what still triggers the prompt in browsers
      // that ignore `preventDefault` alone. The string is never shown.
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  return (
    <DiscardChangesDialog
      open={blocker.status === 'blocked'}
      description="This workflow has unsaved changes. Leaving discards them."
      onDiscard={() => {
        onDiscard?.();
        blocker.proceed?.();
      }}
      onKeep={() => {
        // Not while a save is deciding whether to leave: its answer is what
        // closes the dialog.
        if (saving) return;
        blocker.reset?.();
      }}
      primaryAction={{
        label: 'Save draft and leave',
        pending: saving,
        onSelect: () => {
          // Captured now: the blocker's resolvers belong to this navigation,
          // and the builder may re-render — or remount on the saved version —
          // before the save answers.
          const { proceed, reset } = blocker;
          setSaving(true);
          void onSaveDraft().then((saved) => {
            setSaving(false);
            if (saved) proceed?.();
            else reset?.();
          });
        },
      }}
    />
  );
}
